import React, { useState, useEffect, useCallback, useRef } from "react";
import type { WBSTreeNode } from "../../../models";
import { fetchWBSRoots, fetchWBSChildren } from "../../../api/clientV2";
import "./WBSSidebar.css";

export interface PathEntry {
  id: number;
  name: string;
}

interface Props {
  scheduleId: number;
  selectedNodeId: number | null;
  onSelectNode: (nodeId: number | null) => void;
  path: PathEntry[];
  onPathChange: (path: PathEntry[]) => void;
}

const WBSSidebar: React.FC<Props> = ({
  scheduleId,
  selectedNodeId,
  onSelectNode,
  path,
  onPathChange,
}) => {
  const [children, setChildren] = useState<WBSTreeNode[]>([]);
  const [loading, setLoading] = useState(false);
  const prevPathLenRef = useRef(path.length);

  const loadRoots = useCallback(async () => {
    setLoading(true);
    try {
      const roots = await fetchWBSRoots(scheduleId);
      setChildren(roots);
      onPathChange([]);
    } finally {
      setLoading(false);
    }
  }, [scheduleId, onPathChange]);

  useEffect(() => {
    loadRoots();
  }, [loadRoots]);

  // Detect when path was shortened externally (breadcrumb click)
  useEffect(() => {
    if (path.length < prevPathLenRef.current && path.length > 0) {
      const tipId = path[path.length - 1].id;
      setLoading(true);
      fetchWBSChildren(tipId)
        .then((kids) => setChildren(kids))
        .finally(() => setLoading(false));
    } else if (path.length < prevPathLenRef.current && path.length === 0) {
      fetchWBSRoots(scheduleId)
        .then((roots) => setChildren(roots))
        .finally(() => setLoading(false));
    }
    prevPathLenRef.current = path.length;
  }, [path, scheduleId]);

  const handleDrillDown = async (node: WBSTreeNode) => {
    if (!node.has_children) {
      onSelectNode(node.id);
      return;
    }
    setLoading(true);
    try {
      const kids = await fetchWBSChildren(node.id);
      onPathChange([...path, { id: node.id, name: node.name }]);
      setChildren(kids);
      onSelectNode(node.id);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = async () => {
    if (path.length <= 1) {
      await loadRoots();
      onSelectNode(null);
      return;
    }
    const newPath = path.slice(0, -1);
    const parentId = newPath[newPath.length - 1].id;
    setLoading(true);
    try {
      const kids = await fetchWBSChildren(parentId);
      onPathChange(newPath);
      setChildren(kids);
      onSelectNode(parentId);
    } finally {
      setLoading(false);
    }
  };

  const handleAll = () => {
    loadRoots();
    onSelectNode(null);
  };

  const totalActivities = children.reduce((s, c) => s + c.activity_count, 0);

  return (
    <aside className="wbs-sidebar">
      <div className="wbs-sidebar-header">
        <h3>Work Areas</h3>
        {path.length > 0 && (
          <button className="wbs-back-btn" onClick={handleBack}>
            &larr; Back
          </button>
        )}
      </div>

      {path.length > 0 && (
        <div className="wbs-breadcrumb">
          {path.map((p, i) => (
            <span key={p.id} className="wbs-crumb">
              {i > 0 && " / "}
              {p.name}
            </span>
          ))}
        </div>
      )}

      <button
        className={`wbs-node-btn ${selectedNodeId === null ? "active" : ""}`}
        onClick={handleAll}
      >
        <span className="wbs-node-name">All Activities</span>
        <span className="wbs-node-count">{totalActivities}</span>
      </button>

      {loading ? (
        <div className="wbs-sidebar-loading">Loading...</div>
      ) : (
        children.map((node) => {
          const ss = node.status_summary;
          const total = node.activity_count;
          return (
            <button
              key={node.id}
              className={`wbs-node-btn ${selectedNodeId === node.id ? "active" : ""}`}
              onClick={() => handleDrillDown(node)}
            >
              <div className="wbs-node-top">
                <span className="wbs-node-name">{node.name}</span>
                <span className="wbs-node-count">{node.activity_count}</span>
              </div>
              {total > 0 && (
                <div className="wbs-status-bar">
                  {ss.complete > 0 && (
                    <div
                      className="wbs-status-seg complete"
                      style={{ width: `${(ss.complete / total) * 100}%` }}
                    />
                  )}
                  {ss.active > 0 && (
                    <div
                      className="wbs-status-seg active"
                      style={{ width: `${(ss.active / total) * 100}%` }}
                    />
                  )}
                  {ss.not_started > 0 && (
                    <div
                      className="wbs-status-seg not-started"
                      style={{ width: `${(ss.not_started / total) * 100}%` }}
                    />
                  )}
                </div>
              )}
            </button>
          );
        })
      )}
    </aside>
  );
};

export default WBSSidebar;
